import { Translator } from 'Translator';
import Load from 'components/Load';
import LoadingDots from 'components/LoadingDots';
import useCandidateInfo from 'hooks/useCandidateInfo';
import NavigationableLayout from 'modules/app/components/NavigationableLayout';
import AppBottomPushDown from 'modules/common/Navbar/PushDown/AppBottomPushDown';
import Notfound from 'modules/errorpages/Notfound';
import LoggedOut from 'modules/user-environment2/components/LoggedOut';
import React from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import basePath from 'utils/basePath';
import { useUser } from '~/authentication/store';
import { HOST_STATUS } from '~/constants';

const AsyncTransportationModePage = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-transport-mode" */ './containers/TransportationModePage'
    ),
);
const AsyncLicenseFormPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-license" */ './containers/LicenseFormPage'
    ),
);
const AsyncLanguagesPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-languages" */ './containers/LanguagesPage'
    ),
);
const AsyncExpertisePageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-expertise" */ './containers/ExpertisePage'
    ),
);
const AsyncBasicInfoPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-basic-info" */ './containers/BasicInfoPage'
    ),
);
const AsyncPublicInfoPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-public-info" */ './containers/PublicInfoPage'
    ),
);
const AsyncWebinarPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-webinar-info" */ './containers/WebinarPageContainer'
    ),
);
const AsyncThankYouPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-thank-you-info" */ './containers/ThankYouPageContainer'
    ),
);
const AsyncContactPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-contact-info" */ './containers/ContactInfoPage'
    ),
);
const AsyncProfileManagementPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-management" */ './containers/ProfileManagementPage'
    ),
);
const AsyncIdVerificationPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-id-verification" */ './containers/IdVerificationPageContainer'
    ),
);
const AsyncHostCityPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-management" */ './containers/HostCityPage'
    ),
);

const SubRoutes = () => {
  const { path } = useRouteMatch();
  const { isHost, isAdmin, isLoggedIn, loading } = useUser();
  const { data: candidateInfo, isLoading } = useCandidateInfo();

  if (!loading && !isLoggedIn) {
    return <LoggedOut title={'Login to view this content'} />;
  }

  if (loading || isLoading) {
    return <LoadingDots />;
  }

  const candidateStatus = candidateInfo?.status?.type;
  if (!isHost && !isAdmin && candidateStatus !== HOST_STATUS.ONBOARDING) {
    return (
      <Redirect
        to={generatePathnameWithLangCode('/', Translator.getLanguageCode())}
      />
    );
  }

  return (
    <Switch>
      <NavigationableLayout
        key="host-profile-transport-mode"
        path={`${path}/transportation-mode/`}
        hideFooter
        component={AsyncTransportationModePage}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-license"
        path={`${path}/license/`}
        hideFooter
        component={AsyncLicenseFormPage}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-webinar"
        path={`${path}/webinar/`}
        hideFooter
        component={AsyncWebinarPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-thank-you"
        path={`${path}/thank-you/`}
        hideFooter
        component={AsyncThankYouPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-languages"
        path={`${path}/languages/`}
        hideFooter
        component={AsyncLanguagesPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-expertise"
        path={`${path}/expertise/`}
        hideFooter
        component={AsyncExpertisePageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-basic-info"
        path={`${path}/basic-info/`}
        hideFooter
        component={AsyncBasicInfoPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-public-info"
        path={`${path}/public-info/`}
        hideFooter
        component={AsyncPublicInfoPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-contact-info"
        path={`${path}/contact-info/`}
        hideFooter
        component={AsyncContactPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-city"
        path={`${path}/city/`}
        hideFooter
        component={AsyncHostCityPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-id-verification"
        path={`${path}/id-verification/`}
        hideFooter
        component={AsyncIdVerificationPageContainer}
        noNavbar
        nonFixedNavbar
      />
      <NavigationableLayout
        key="host-profile-management"
        path={`${path}/`}
        hideFooter
        component={AsyncProfileManagementPageContainer}
      />
      <Route>
        <Notfound />
      </Route>
    </Switch>
  );
};

const routes = (
  <Route path={`${basePath}/host/profile`}>
    <>
      <SubRoutes />
      <AppBottomPushDown />
    </>
  </Route>
);

export default routes;
